import React, { useState, useRef, useContext } from "react";
import { pdf } from "@react-pdf/renderer";
import ChangeOfStatusPDF from "../PDF Functions/ChangeOfStatusPDF";
import "../TruckServices/ChecklistForm.css";
import SignatureCanvas from "react-signature-canvas";
import { AuthContext } from "../hooks/AuthProvider";


function ChangeOfStatusForm() {
  const { currentUser } = useContext(AuthContext); 
  const sigCanvasRef = useRef();
  const [formData, setFormData] = useState({
    employeeName: "",
    payId: "",
    effectiveDate: "",
    currentPayRate: { hour: "", week: "", annual: "" },
    newPayRate: { hour: "", week: "", annual: "" },
    reasonForPayChange: "",
    statusEffectiveDate: "",
    division: "",
    state: "",
    classification: {
      seasonal: false,
      nonSeasonal: false,
      partTime: false,
      fullTime: false,
    },
    statusChange: {
      promotion: false,
      demotion: false,
      vehicleAllowance: "",
      cellAllowance: "",
    },
    employmentClass: {
      laborer: false,
      driver: false,
      supervisor: false,
      superintendent: false,
      equipmentOperator: false,
      manager: false,
      clerical: false,
      other: "",
    },
    vacationDays: "",
    jobChangeReason: "",
    layoff: {
      jobAttached: false,
      seasonalLeave: false,
      returnDate: "",
      fmla: false,
    },
    divisionApproval: {
      name: "",
      signature: "",
    },
    personalChange: {
      newName: "",
      newAddress: "",
      newPhone: "",
      emergencyContact: "",
      emergencyPhoneW: "",
      emergencyPhoneH: "",
      emergencyPhoneCell: "",
    },
    termination: {
      lastWorkDate: "",
      rehire: "",
      reason: [],
      otherReason: "",
      companyAssets: {
        returned: "",
        notReturned: "",
      },
      comments: "",
    },
    supervisor: {
      name: "",
      date: "",
      signature: "",
    },
    employeeSignature: {
      signature: "",
      date: "",
    },
    hrDept: {
      name: "",
      dateFiled: "",
    },
  });

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleCheckboxGroup = (section, field) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section][field],
      },
    }));
  };

  const handleMultiCheckbox = (fieldName, value) => {
    setFormData(prev => {
      const exists = prev.termination.reason.includes(value);
      const newSet = exists
        ? prev.termination.reason.filter(v => v !== value)
        : [...prev.termination.reason, value];
      return {
        ...prev,
        termination: {
          ...prev.termination,
          reason: newSet,
        },
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const pdfBlob = await pdf(
        <ChangeOfStatusPDF formData={formData} />
      ).toBlob();

      const arrayBuffer = await pdfBlob.arrayBuffer();
      const pdfBase64 = btoa(
        new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), "")
      );

      const emailData = {
        to: ["payroll@rotoloconsultants.com", "humanresources@rotoloconsultants.com"],
        from: "serviceupdate@rotoloconsultants.com",
        subject: `Change of Status Form - ${formData.employeeName || "Unnamed"}`,
        text: `A change of status form has been submitted.`,
        html: `<p>A change of status form has been submitted for <strong>${formData.employeeName || "an employee"}</strong> by <strong>${currentUser?.email || "Unknown User"}</strong>.</p>`,
        attachments: [
          {
            content: pdfBase64,
            filename: "change_of_status.pdf",
            type: "application/pdf",
            disposition: "attachment",
          },
        ],
      };

      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailData }),
      });

      if (response.ok) {
        alert("Change of Status form emailed successfully!");
      } else {
        throw new Error("Email failed to send");
      }
    } catch (err) {
      console.error("❌ Error sending form:", err);
      alert("Failed to email the form. Please try again.");
    }
  };

  return (
    <form className="checklist-form" onSubmit={handleSubmit}>
      <h1 className="form-title">Employee Change of Status Report</h1>

      <div className="form-group">
        <label>Employee Name</label>
        <input type="text" className="form-control" value={formData.employeeName} onChange={e => setFormData({ ...formData, employeeName: e.target.value })} />
      </div>

      <h2 className="section-title">Change in Pay Rate</h2>
      <div className="form-group">
        <label>Pay ID</label>
        <input type="text" className="form-control" value={formData.payId} onChange={e => setFormData({ ...formData, payId: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Effective Date of Change</label>
        <input type="date" className="form-control" value={formData.effectiveDate} onChange={e => setFormData({ ...formData, effectiveDate: e.target.value })} />
      </div>

      {[{ key: "hour", label: "Hour" }, { key: "week", label: "Week" }, { key: "annual", label: "Annual" }].map(({ key, label }) => (
        <div className="form-group" key={key}>
          <label>Current Pay Rate ({label})</label>
          <input type="text" className="form-control" value={formData.currentPayRate[key]} onChange={e => handleChange("currentPayRate", key, e.target.value)} />
          <label>New Pay Rate ({label})</label>
          <input type="text" className="form-control" value={formData.newPayRate[key]} onChange={e => handleChange("newPayRate", key, e.target.value)} />
        </div>
      ))}

      <div className="form-group">
        <label>Reason for Change</label>
        <textarea className="form-control" value={formData.reasonForPayChange} onChange={e => setFormData({ ...formData, reasonForPayChange: e.target.value })} />
      </div>

      <h2 className="section-title">Change in Job Status</h2>
      <div className="form-group">
        <label>Effective Date of Change</label>
        <input type="date" className="form-control" value={formData.statusEffectiveDate} onChange={e => setFormData({ ...formData, statusEffectiveDate: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Division</label>
        <input type="text" className="form-control" value={formData.division} onChange={e => setFormData({ ...formData, division: e.target.value })} />
        <label>State</label>
        <input type="text" className="form-control" value={formData.state} onChange={e => setFormData({ ...formData, state: e.target.value })} />
      </div>

      <div className="form-group">
  <label>Classification</label>
  <select
    className="form-control"
    value={Object.keys(formData.classification).find(key => formData.classification[key]) || ""}
    onChange={e =>
      setFormData(prev => ({
        ...prev,
        classification: {
          seasonal: false,
          nonSeasonal: false,
          partTime: false,
          fullTime: false,
          [e.target.value]: true,
        },
      }))
    }
  >
    <option value="">-- Select --</option>
    <option value="seasonal">Seasonal</option>
    <option value="nonSeasonal">Non-Seasonal</option>
    <option value="partTime">Part-Time</option>
    <option value="fullTime">Full-Time</option>
  </select>
</div>


      <div className="form-group">
  <label>Status Change</label>
  <select
    className="form-control"
    value={formData.statusChange.promotion ? "promotion" : formData.statusChange.demotion ? "demotion" : ""}
    onChange={e =>
      setFormData(prev => ({
        ...prev,
        statusChange: {
          ...prev.statusChange,
          promotion: e.target.value === "promotion",
          demotion: e.target.value === "demotion",
        },
      }))
    }
  >
    <option value="">-- Select --</option>
    <option value="promotion">Promotion</option>
    <option value="demotion">Demotion</option>
  </select>
</div>


      <div className="form-group">
        <label>Vehicle Allowance ($)</label>
        <input type="text" className="form-control" value={formData.statusChange.vehicleAllowance} onChange={e => handleChange("statusChange", "vehicleAllowance", e.target.value)} />
        <label>Cell Allowance ($)</label>
        <input type="text" className="form-control" value={formData.statusChange.cellAllowance} onChange={e => handleChange("statusChange", "cellAllowance", e.target.value)} />
      </div>

      <div className="form-group">
  <label>Employment Classification</label>
  <select
    className="form-control"
    value={Object.keys(formData.employmentClass).find(key => formData.employmentClass[key] === true && key !== "other") || ""}
    onChange={e =>
      setFormData(prev => ({
        ...prev,
        employmentClass: {
          ...Object.fromEntries(Object.keys(prev.employmentClass).map(k => [k, false])),
          [e.target.value]: true,
          other: "",
        },
      }))
    }
  >
    <option value="">-- Select --</option>
    <option value="laborer">Laborer</option>
    <option value="driver">Driver</option>
    <option value="supervisor">Supervisor</option>
    <option value="superintendent">Superintendent</option>
    <option value="equipmentOperator">Equipment Operator</option>
    <option value="manager">Manager</option>
    <option value="clerical">Clerical</option>
    <option value="other">Other</option>
  </select>
  {formData.employmentClass.other !== "" && (
    <input
      type="text"
      placeholder="Specify other"
      className="form-control"
      value={formData.employmentClass.other}
      onChange={e => handleChange("employmentClass", "other", e.target.value)}
    />
  )}
</div>


      <div className="form-group">
        <label>Other</label>
        <input type="text" className="form-control" value={formData.employmentClass.other} onChange={e => handleChange("employmentClass", "other", e.target.value)} />
      </div>

      <div className="form-group">
        <label>Vacation Days</label>
        <input type="number" className="form-control" value={formData.vacationDays} onChange={e => setFormData({ ...formData, vacationDays: e.target.value })} />
      </div>

      <div className="form-group">
        <label>Reason for Change</label>
        <textarea className="form-control" value={formData.jobChangeReason} onChange={e => setFormData({ ...formData, jobChangeReason: e.target.value })} />
      </div>

      <div className="form-group">
  <label>Layoff / Leave Types</label>
  <select
    multiple
    className="form-control"
    value={Object.entries(formData.layoff)
      .filter(([key, val]) => val === true && key !== "returnDate")
      .map(([key]) => key)}
    onChange={e => {
      const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
      setFormData(prev => ({
        ...prev,
        layoff: {
          ...prev.layoff,
          jobAttached: selected.includes("jobAttached"),
          seasonalLeave: selected.includes("seasonalLeave"),
          fmla: selected.includes("fmla"),
        },
      }));
    }}
  >
    <option value="jobAttached">Job Attached</option>
    <option value="seasonalLeave">Seasonal Leave</option>
    <option value="fmla">FMLA</option>
  </select>
</div>


      <div className="form-group">
        <label>Return Date</label>
        <input type="date" className="form-control" value={formData.layoff.returnDate} onChange={e => handleChange("layoff", "returnDate", e.target.value)} />
      </div>

      <h2 className="section-title">Personal Info Changes</h2>
      {[{ key: "newName", label: "New Name" }, { key: "newAddress", label: "New Address" }, { key: "newPhone", label: "New Phone" }, { key: "emergencyContact", label: "Emergency Contact" }, { key: "emergencyPhoneW", label: "Emergency Work Phone" }, { key: "emergencyPhoneH", label: "Emergency Home Phone" }, { key: "emergencyPhoneCell", label: "Emergency Cell Phone" }].map(({ key, label }) => (
        <div className="form-group" key={key}>
          <label>{label}</label>
          <input type="text" className="form-control" value={formData.personalChange[key]} onChange={e => handleChange("personalChange", key, e.target.value)} />
        </div>
      ))}

      <h2 className="section-title">Employee Termination</h2>
      <div className="form-group">
        <label>Last Work Date</label>
        <input type="date" className="form-control" value={formData.termination.lastWorkDate} onChange={e => handleChange("termination", "lastWorkDate", e.target.value)} />
        <label>Rehire?</label>
        <select className="form-control" value={formData.termination.rehire} onChange={e => handleChange("termination", "rehire", e.target.value)}>
          <option value="">--Select--</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>
      <div className="form-group">
  <label>Reason(s) for Termination</label>
  <select
    multiple
    className="form-control"
    value={formData.termination.reason}
    onChange={e => {
      const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
      setFormData(prev => ({
        ...prev,
        termination: {
          ...prev.termination,
          reason: selected,
        },
      }));
    }}
  >
    {[
      "Lay-Off", "Voluntary Quit", "Injury", "Seeking/Other Employment", "Alcohol",
      "Absenteeism", "Unacceptable Performance", "Retirement", "NCNS", "Other"
    ].map(reason => (
      <option key={reason} value={reason}>{reason}</option>
    ))}
  </select>
  <input
    className="form-control"
    placeholder="If Other, describe"
    value={formData.termination.otherReason}
    onChange={e => handleChange("termination", "otherReason", e.target.value)}
  />
</div>


      <h2 className="section-title">Approvals</h2>
      <div className="form-group">
        <label>Supervisor Name</label>
        <input type="text" className="form-control" value={formData.supervisor.name} onChange={e => handleChange("supervisor", "name", e.target.value)} />
        <label>Date</label>
        <input type="date" className="form-control" value={formData.supervisor.date} onChange={e => handleChange("supervisor", "date", e.target.value)} />
      </div>
      <div className="form-group">
        <label>Supervisor Signature</label>
  <SignatureCanvas
    penColor="black"
    canvasProps={{ width: 400, height: 150, className: "sigCanvas" }}
    ref={sigCanvasRef}
    onEnd={() => {
      const dataURL = sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");
      handleChange("supervisor", "signature", dataURL);
    }}
  />
  
  <button
    type="button"
    className="clear-button"
    onClick={() => {
      sigCanvasRef.current.clear();
      handleChange("supervisor", "signature", "");
    }}
  >
    Clear Signature
  </button>
</div>
      <div className="form-group">
        <label>Date Filed</label>
        <input type="date" className="form-control" value={formData.hrDept.dateFiled} onChange={e => handleChange("hrDept", "dateFiled", e.target.value)} />
      </div>

      <button type="submit" className="submit-button">Submit Form</button>
    </form>
  );
}

export default ChangeOfStatusForm;
